import type { InlineTextStyle } from '../model/textDocument';

type NotifyChange = () => void;
type ApplyInlineStyle = (style: InlineTextStyle) => void;

let activeEditable: HTMLElement | null = null;
let notifyChange: NotifyChange | null = null;
let savedRange: Range | null = null;
let applyInlineStyle: ApplyInlineStyle | null = null;

export function registerActiveTextEditor(editable: HTMLElement, onChange: NotifyChange): () => void {
  activeEditable = editable;
  notifyChange = onChange;
  savedRange = null;
  return () => {
    if (activeEditable === editable) {
      activeEditable = null;
      notifyChange = null;
      savedRange = null;
    }
  };
}

export function registerActiveTextEditorCommands(applyStyle: ApplyInlineStyle): () => void {
  applyInlineStyle = applyStyle;
  return () => {
    if (applyInlineStyle === applyStyle) applyInlineStyle = null;
  };
}

export function saveActiveTextSelection(): void {
  if (!activeEditable) return;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const { anchorNode, focusNode } = selection;
  if (!anchorNode || !focusNode || !activeEditable.contains(anchorNode) || !activeEditable.contains(focusNode)) return;
  savedRange = selection.getRangeAt(0).cloneRange();
}

function restoreActiveTextSelection(): void {
  if (!savedRange) return;
  const selection = window.getSelection();
  if (!selection) return;
  selection.removeAllRanges();
  selection.addRange(savedRange);
}

function runEditorStyle(style: InlineTextStyle): boolean {
  if (!applyInlineStyle) return false;
  activeEditable?.focus();
  restoreActiveTextSelection();
  applyInlineStyle(style);
  notifyChange?.();
  return true;
}

export function applyTextEditorBold(): boolean {
  return runEditorStyle({ bold: true });
}

export function applyTextEditorItalic(): boolean {
  return runEditorStyle({ italic: true });
}

export function applyTextEditorUnderline(): boolean {
  return runEditorStyle({ underline: true });
}

export function applyTextEditorStrike(): boolean {
  return runEditorStyle({ strike: true });
}

export function applyTextEditorColor(color: string): boolean {
  return runEditorStyle({ color });
}

export function applyTextEditorBackgroundColor(color: string): boolean {
  return runEditorStyle({ backgroundColor: color });
}
